import { type FC, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/shared/lib/utils";

const COLOR_STYLES = {
  green: "bg-green-50 text-green-600 border border-green-300",
  blue: "bg-blue-50 text-blue-600 border border-blue-300",
  yellow: "bg-yellow-100 text-yellow-600 border border-yellow-400",
  stone: "bg-stone-100 text-stone-700 border border-stone-300",
  salmon: "bg-red-50 text-red-400 border border-red-200",
  orange: "bg-orange-50 text-orange-600 border border-orange-300",
  purple: "bg-purple-50 text-purple-600 border border-purple-300",
  brand: "bg-brand text-white",
  white: "bg-white text-secondary border border-primary",
} as const;

const SIZE_STYLES = {
  sm: { container: "px-1.5 py-0.5 text-xs gap-1", icon: "h-3 w-3" },
  md: { container: "px-2 py-1 text-sm gap-1", icon: "h-3.5 w-3.5" },
} as const;

export type TagColor = keyof typeof COLOR_STYLES;
export type TagSize = keyof typeof SIZE_STYLES;

interface TagProps {
  children: ReactNode;
  color?: TagColor;
  size?: TagSize;
  icon?: LucideIcon;
  iconStroke?: number;
  iconClassName?: string;
  className?: string;
}

export const Tag: FC<TagProps> = ({
  children,
  color = "green",
  size = "md",
  icon: Icon,
  iconStroke = 2,
  iconClassName,
  className,
}) => {
  const sizeClasses = SIZE_STYLES[size];

  return (
    <div
      className={cn(
        "inline-flex items-center rounded-full font-medium max-w-full whitespace-nowrap box-border",
        sizeClasses.container,
        COLOR_STYLES[color],
        className,
      )}
    >
      {Icon && (
        <Icon
          strokeWidth={iconStroke}
          className={cn(sizeClasses.icon, "shrink-0", iconClassName)}
        />
      )}
      <span className="truncate">{children}</span>
    </div>
  );
};
